import { useTranslation } from 'react-i18next';
import {
  SCOPE_LEVEL_COLORS,
  type GraphScopeHighlightInput,
  type MemoryGraphNodeType,
} from './memoryBrainRegions';

type MemoryGraphScopeLegendProps = {
  nodes: GraphScopeHighlightInput[];
  highlightStation: string | null;
  highlightDevice: string | null;
  onSelectStation: (id: string | null) => void;
  onSelectDevice: (id: string | null) => void;
  compact?: boolean;
};

function getNodeLabel(node: GraphScopeHighlightInput): string {
  return node.id;
}

function filterByType(
  nodes: GraphScopeHighlightInput[],
  type: MemoryGraphNodeType,
): GraphScopeHighlightInput[] {
  return nodes.filter((n) => n.type === type);
}

export default function MemoryGraphScopeLegend({
  nodes,
  highlightStation,
  highlightDevice,
  onSelectStation,
  onSelectDevice,
  compact = false,
}: MemoryGraphScopeLegendProps) {
  const { t } = useTranslation();

  const stationNodes = filterByType(nodes, 'station');
  const deviceNodes = filterByType(nodes, 'device');

  const anyHighlight = highlightStation !== null || highlightDevice !== null;

  return (
    <div
      className={`shrink-0 border-b border-app-border/60 bg-app-muted/15 ${
        compact ? 'px-2 py-1.5' : 'px-3 py-2'
      }`}
      role="group"
      aria-label={t('memory.scopeLegend.title', { defaultValue: 'กรองตาม Scope' })}
    >
      {/* Header */}
      <div className={compact ? 'mb-1' : 'mb-1.5'}>
        <p className={`font-semibold text-app-text ${compact ? 'text-[10px]' : 'text-xs'}`}>
          {t('memory.scopeLegend.title', { defaultValue: 'กรองตาม Scope' })}
        </p>
        <p className="text-[9px] text-app-text-dim leading-snug mt-0.5">
          {t('memory.scopeLegend.graphNote', {
            defaultValue: 'สีโหนด = scope · คลิก station หรือ device เพื่อไฮไลต์',
          })}
        </p>
        {anyHighlight && (
          <button
            type="button"
            className="mt-1 text-[9px] font-medium text-app-accent underline-offset-2 hover:underline"
            onClick={() => {
              onSelectStation(null);
              onSelectDevice(null);
            }}
          >
            {t('memory.scopeLegend.clearFilter', { defaultValue: 'ล้างตัวกรอง' })}
          </button>
        )}
      </div>

      {/* Station section */}
      {stationNodes.length > 0 && (
        <div className="mb-1.5">
          <p className={`mb-0.5 font-medium text-app-text-dim uppercase tracking-wide ${compact ? 'text-[8px]' : 'text-[9px]'}`}>
            {t('memory.scopeLegend.stationSection', { defaultValue: 'สถานี' })}
          </p>
          <div className="flex flex-wrap gap-1">
            {stationNodes.map((node) => {
              const selected = highlightStation === node.id;
              const color = SCOPE_LEVEL_COLORS[node.scope];
              return (
                <button
                  key={node.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onSelectStation(selected ? null : node.id)}
                  className={`flex items-center gap-1 rounded-md border text-left transition-colors ${
                    compact ? 'px-1.5 py-1' : 'px-2 py-1.5'
                  } ${
                    selected
                      ? 'border-app-accent bg-app-accent/10 ring-1 ring-app-accent/40'
                      : 'border-app-border bg-app-surface/80 hover:bg-app-surface cursor-pointer'
                  }`}
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full ring-1 ring-black/10"
                    style={{ backgroundColor: color }}
                    aria-hidden
                  />
                  <span className={`font-semibold text-app-text truncate ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
                    {getNodeLabel(node)}
                  </span>
                  {selected && (
                    <span className="text-[8px] font-medium uppercase tracking-wide text-app-accent">
                      {t('memory.scopeLegend.filterActive', { defaultValue: 'กำลังไฮไลต์' })}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Device section */}
      {deviceNodes.length > 0 && (
        <div>
          <p className={`mb-0.5 font-medium text-app-text-dim uppercase tracking-wide ${compact ? 'text-[8px]' : 'text-[9px]'}`}>
            {t('memory.scopeLegend.deviceSection', { defaultValue: 'อุปกรณ์' })}
          </p>
          <div className={`grid gap-1 ${compact ? 'grid-cols-3 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-6'}`}>
            {deviceNodes.map((node) => {
              const selected = highlightDevice === node.id;
              const color = SCOPE_LEVEL_COLORS[node.scope];
              return (
                <button
                  key={node.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onSelectDevice(selected ? null : node.id)}
                  className={`flex items-center gap-1 rounded-md border text-left transition-colors ${
                    compact ? 'px-1.5 py-1' : 'px-2 py-1.5'
                  } ${
                    selected
                      ? 'border-app-accent bg-app-accent/10 ring-1 ring-app-accent/40'
                      : 'border-app-border bg-app-surface/80 hover:bg-app-surface cursor-pointer'
                  }`}
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full ring-1 ring-black/10"
                    style={{ backgroundColor: color }}
                    aria-hidden
                  />
                  <span className={`truncate font-semibold text-app-text ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
                    {getNodeLabel(node)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {stationNodes.length === 0 && deviceNodes.length === 0 && (
        <p className="text-[9px] text-app-text-dim">
          {t('memory.scopeLegend.notInGraph', { defaultValue: 'ไม่มีโหนดในกราฟนี้' })}
        </p>
      )}
    </div>
  );
}
